import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import DatabasesSection from './DatabasesSection';
import AllDocumentsView from './AllDocumentsView';

const Home: React.FC = () => {
  const [activeSection, setActiveSection] = useState<'documents' | 'databases'>('documents');
  const [documentId, setDocumentId] = useState('');
  const allDocumentsViewRef = useRef<{ refreshDocuments: () => void }>(null);
  const navigate = useNavigate();

  const handleOpenDocument = () => {
    if (documentId.trim()) {
      navigate(`/${documentId.trim()}`);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleOpenDocument();
    }
  };

  const handleRefreshDocuments = () => {
    allDocumentsViewRef.current?.refreshDocuments();
  };

  return (
    <div className="home-container">
      <div className="sidebar">
        <header className="sidebar-header">
          <h1>📄 Object-Property-LWW</h1>
          <p>Document-based storage with Last-Writer-Wins</p>
        </header>

        <nav className="sidebar-nav">
          <button 
            className={`nav-item ${activeSection === 'documents' ? 'active' : ''}`}
            onClick={() => setActiveSection('documents')}
          >
            <span className="nav-icon">📁</span>
            <span className="nav-label">Documents</span>
          </button>
          <button 
            className={`nav-item ${activeSection === 'databases' ? 'active' : ''}`}
            onClick={() => setActiveSection('databases')}
          >
            <span className="nav-icon">🗄️</span>
            <span className="nav-label">Databases</span>
          </button>
        </nav>
      </div>

      <main className="main-content">
        {activeSection === 'documents' && (
          <section className="content-section">
            <header className="section-header">
              <h2>📁 Documents</h2>
              <p>Manage and access your documents</p>
            </header>
            
            <div className="create-document-section">
              <h3>Load or Create Document</h3>
              <div className="input-group">
                <input
                  type="text"
                  placeholder="Enter document ID (e.g., my-notes, project-plan)"
                  value={documentId}
                  onChange={(e) => setDocumentId(e.target.value)}
                  onKeyPress={handleKeyPress}
                  className="document-input"
                />
                <button 
                  onClick={handleOpenDocument}
                  disabled={!documentId.trim()}
                  className="open-create-button"
                >
                  Load or Create
                </button>
              </div>
              <p className="input-help">
                If the document exists locally or remotely, you'll be able to edit it. If not, you'll have options to create it locally or download from server.
              </p>
            </div>

            <div className="documents-list-section">
              <div className="documents-header">
                <h3>Local Documents</h3>
                <button onClick={handleRefreshDocuments} className="refresh-btn" title="Refresh">
                  🔄
                </button>
              </div>
              <AllDocumentsView ref={allDocumentsViewRef} />
            </div>
          </section>
        )}

        {activeSection === 'databases' && (
          <DatabasesSection />
        )}
      </main>

      <style>{`
        .home-container {
          display: flex;
          height: 100vh;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
          background-color: #f8f9fa;
        }

        .sidebar {
          width: 280px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          display: flex;
          flex-direction: column;
          box-shadow: 2px 0 10px rgba(0, 0, 0, 0.1);
        }

        .sidebar-header {
          padding: 2rem 1.5rem 1.5rem;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }

        .sidebar-header h1 {
          margin: 0 0 0.5rem 0;
          font-size: 1.5rem;
          font-weight: 700;
          color: white;
        }

        .sidebar-header p {
          margin: 0;
          font-size: 0.9rem;
          opacity: 0.8;
          line-height: 1.4;
        }

        .sidebar-nav {
          flex: 1;
          padding: 1rem 0;
        }

        .nav-item {
          width: 100%;
          display: flex;
          align-items: center;
          padding: 1rem 1.5rem;
          background: none;
          border: none;
          color: white;
          cursor: pointer;
          transition: all 0.2s ease;
          text-align: left;
          position: relative;
        }

        .nav-item:hover {
          background-color: rgba(255, 255, 255, 0.1);
        }

        .nav-item.active {
          background-color: rgba(255, 255, 255, 0.15);
        }

        .nav-item.active::before {
          content: '';
          position: absolute;
          left: 0;
          top: 0;
          bottom: 0;
          width: 4px;
          background-color: white;
        }

        .nav-icon {
          font-size: 1.25rem;
          margin-right: 0.75rem;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 24px;
        }

        .nav-label {
          font-size: 1rem;
          font-weight: 500;
        }

        .main-content {
          flex: 1;
          overflow-y: auto;
          background-color: white;
        }

        .content-section {
          max-width: 1200px;
          margin: 0 auto;
          padding: 2rem;
          animation: fadeIn 0.3s ease-in;
        }

        .section-header {
          margin-bottom: 2rem;
        }

        .section-header h2 {
          color: #2c3e50;
          margin: 0 0 0.5rem 0;
          font-size: 2rem;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .section-header p {
          color: #7f8c8d;
          margin: 0;
          font-size: 1.1rem;
        }

        .create-document-section {
          padding: 2rem;
          background: #f8f9fa;
          border-radius: 12px;
          border-left: 4px solid #3498db;
          margin-bottom: 3rem;
        }

        .documents-list-section {
          margin-top: 2rem;
        }

        .documents-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 1rem;
        }

        .documents-header h3 {
          margin: 0;
          color: #2c3e50;
        }

        .refresh-btn {
          background: #95a5a6;
          border: none;
          border-radius: 50%;
          width: 32px;
          height: 32px;
          font-size: 0.875rem;
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .refresh-btn:hover {
          background: #7f8c8d;
          transform: rotate(90deg);
        }

        .documents-list-section h3 {
          margin: 0 0 1.5rem 0;
          color: #2c3e50;
          font-size: 1.3rem;
          font-weight: 600;
        }

        .create-document-section h3 {
          margin: 0 0 1.5rem 0;
          color: #2c3e50;
          font-size: 1.3rem;
          font-weight: 600;
        }

        .input-group {
          display: flex;
          gap: 0.75rem;
          margin-bottom: 0.75rem;
        }

        .document-input {
          flex: 1;
          padding: 0.75rem 1rem;
          border: 2px solid #e1e5e9;
          border-radius: 8px;
          font-size: 1rem;
          transition: all 0.2s ease;
          background: white;
        }

        .document-input:focus {
          outline: none;
          border-color: #3498db;
          box-shadow: 0 0 0 3px rgba(52, 152, 219, 0.1);
        }

        .open-create-button {
          padding: 0.75rem 1.5rem;
          background: linear-gradient(135deg, #3498db, #2980b9);
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          white-space: nowrap;
        }

        .open-create-button:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(52, 152, 219, 0.3);
        }

        .open-create-button:disabled {
          background: #bdc3c7;
          cursor: not-allowed;
          transform: none;
          box-shadow: none;
        }

        .input-help {
          color: #7f8c8d;
          font-size: 0.9rem;
          line-height: 1.4;
          margin: 0;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @media (max-width: 768px) {
          .home-container {
            flex-direction: column;
            height: auto;
            min-height: 100vh;
          }

          .sidebar {
            width: 100%;
            position: relative;
          }

          .sidebar-header {
            padding: 1.5rem 1rem 1rem;
          }

          .sidebar-header h1 {
            font-size: 1.3rem;
          }

          .sidebar-nav {
            display: flex;
            padding: 0;
            border-top: 1px solid rgba(255, 255, 255, 0.1);
          }

          .nav-item {
            flex: 1;
            padding: 1rem 0.5rem;
            justify-content: center;
          }

          .nav-item.active::before {
            left: 0;
            right: 0;
            top: auto;
            bottom: 0;
            width: auto;
            height: 4px;
          }

          .nav-icon {
            margin-right: 0.5rem;
          }

          .nav-label {
            font-size: 0.9rem;
          }

          .content-section {
            padding: 1.5rem 1rem;
          }

          .section-header h2 {
            font-size: 1.5rem;
          }

          .create-document-section {
            margin-top: 2rem;
            padding: 1.5rem;
          }

          .input-group {
            flex-direction: column;
          }

          .open-create-button {
            align-self: stretch;
          }
        }
      `}</style>
    </div>
  );
};

export default Home;